import {
  Box,
  Button,
  Checkbox,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DateTimeUtils, { FORMAT_TYPE } from "../../utils/DateTimeUtils";
import OrderRes from "../../types/res/order/OrderRes";
import { putCollection, putPayoff } from "redux/actions/payoff";
import { useDispatch, useSelector } from "react-redux";
import CastRes from "../../types/res/cast/CastRes";
import PaymentType from "../../types/enum/PaymentType";
import EnumUtils from "../../utils/EnumUtils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import SpecPdf from "./specPdf";
import PageUtils from "../../utils/PageUtils";
import OrderStatus from "../../types/enum/OrderStatus";

type Props = {
  startDate: Date;
  endDate: Date;
  cast: CastRes;
  orders: OrderRes[];
};
const PayoffTable: React.FC<Props> = ({ startDate, endDate, cast, orders }) => {
  const { t } = useTranslation("settlement");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const [message, setMessage] = useState("");
  const [completePayoff, setCompletePayoff] = useState<OrderRes[]>([]);
  const [loading, setLoading] = useState(false);
  const specPdfRef = useRef<HTMLDivElement>(null);

  const handleCollectionChange = (order: OrderRes) => async () => {
    if (
      EnumUtils.mapToEnum(OrderStatus, order.status) === OrderStatus.booking
    ) {
      const result = window.confirm(
        t("alerts.confirmChangeToComplete")
      );
      if (!result) {
        return false;
      }
    }
    if (order.collectReceivable) {
      const result = window.confirm(
        t("alerts.confirmResetPoint")
      );
      if (!result) {
        return false;
      }
    }
    dispatch(putCollection(companyId, order.orderId));
  };

  const handlePayoffChange = (orderId: number) => async () => {
    dispatch(putPayoff(companyId, orderId));
  };
  useEffect(() => {
    setCompletePayoff([
      ...orders.filter((order) => order.collectReceivable && order.payoff),
    ]);
  }, [orders]);

  useEffect(() => {
    if (!orders?.length) return;
  }, [orders, message]);

  const downloadSpecPdf = async () => {
    if (!specPdfRef.current) return;
    setLoading(true);
    try {
      const canvas = await html2canvas(specPdfRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      // 横長 A3 の用紙サイズ (pt) — キャプチャ比率で PDF サイズを決める
      const pdfDoc = new jsPDF({
        orientation: imgWidth >= imgHeight ? "landscape" : "portrait",
        unit: "px",
        format: [imgWidth, imgHeight],
        hotfixes: ["px_scaling"],
      });
      pdfDoc.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      const fileName = `${t("pdf.fileNamePrefix")}_${cast.displayName}_${DateTimeUtils.toFormatAsLocalTimezone(
        startDate,
        FORMAT_TYPE.YEAR_DAY
      )}_${DateTimeUtils.toFormatAsLocalTimezone(
        endDate,
        FORMAT_TYPE.YEAR_DAY
      )}.pdf`;
      const blob = pdfDoc.output("blob");
      PageUtils.download(blob, fileName);
    } catch (err) {
      console.error("PDF generation error:", err);
      alert(t("alerts.pdfGenerationFailed"));
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Box display="flex" marginTop={2}>
        <Typography variant="subtitle1">{cast.displayName}</Typography>
        <Typography variant="subtitle1" style={{ marginLeft: "10px" }}>
          {DateTimeUtils.toFormatAsLocalTimezone(
            startDate,
            FORMAT_TYPE.YEAR_DAY
          )}
          〜
          {DateTimeUtils.toFormatAsLocalTimezone(endDate, FORMAT_TYPE.YEAR_DAY)}
        </Typography>
      </Box>
      {orders.length ? (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("columns.date")}</TableCell>
                  <TableCell>{t("columns.guestName")}</TableCell>
                  <TableCell>{t("columns.guestTel")}</TableCell>
                  <TableCell>{t("columns.shopName")}</TableCell>
                  <TableCell>{t("columns.castName")}</TableCell>
                  <TableCell>{t("columns.course")}</TableCell>
                  <TableCell>{t("columns.status")}</TableCell>
                  <TableCell>{t("columns.operation")}</TableCell>
                  <TableCell>{t("columns.payment")}</TableCell>
                  <TableCell>{t("columns.shopSales")}</TableCell>
                  <TableCell>{t("columns.castReward")}</TableCell>
                  <TableCell>{t("columns.totalSales")}</TableCell>
                  <TableCell padding="checkbox">{t("columns.collectReceivable")}</TableCell>
                  <TableCell padding="checkbox">{t("columns.payoff")}</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.orderId}>
                    <TableCell>
                      {DateTimeUtils.toFormatAsLocalTimezone(
                        order.orderDate,
                        FORMAT_TYPE.YEAR_DAY
                      )}
                    </TableCell>
                    <TableCell>{order.guest?.name || ""}</TableCell>
                    <TableCell>{order.guest?.tel || ""}</TableCell>
                    <TableCell>{order.shop?.name || ""}</TableCell>
                    <TableCell>{order.castName?.name || ""}</TableCell>
                    <TableCell>{order.course?.name || ""}</TableCell>
                    <TableCell>
                      {EnumUtils.mapToEnum(OrderStatus, order.status)}
                    </TableCell>
                    <TableCell>
                      {order.actualInTime && order.actualEndTime
                        ? t("operationStatus.completed")
                        : t("operationStatus.notRegistered")}
                    </TableCell>
                    <TableCell>
                      {!order.paymentType
                        ? t("payment.unset")
                        : EnumUtils.mapToEnum(
                            PaymentType,
                            order.paymentType
                          ) === PaymentType.cash
                        ? t("payment.cash")
                        : t("payment.creditCard")}
                    </TableCell>
                    <TableCell align="right">
                      <Typography>{order.totalShopFee || 0}{t("units.yen")}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography>{order.totalCastFee || 0}{t("units.yen")}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography>{order.totalFee || 0}{t("units.yen")}</Typography>
                    </TableCell>
                    <TableCell padding="checkbox">
                      <Checkbox
                        name="collection"
                        checked={order.collectReceivable}
                        onChange={handleCollectionChange(order)}
                      />
                    </TableCell>
                    <TableCell padding="checkbox">
                      <Checkbox
                        name="payoff"
                        checked={order.payoff}
                        onChange={handlePayoffChange(order.orderId)}
                        disabled={
                          EnumUtils.mapToEnum(OrderStatus, order.status) !==
                          OrderStatus.paid
                        }
                      />
                    </TableCell>
                    <TableCell padding="checkbox">
                      <Link href={`/order/${order.orderId}`}>{t("actions.edit")}</Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            margin={2}
          >
            <Box display="flex" flexBasis="60%">
              <TextField
                multiline
                variant="outlined"
                label={t("message.castMessageLabel")}
                style={{ width: "50%" }}
                onBlur={(event) => setMessage(event.target.value)}
              />
              {/*<Button*/}
              {/*  style={{ marginLeft: "10px" }}*/}
              {/*  variant="outlined"*/}
              {/*  onClick={recipePdf(completePayoff, cast, totalCastFee)}*/}
              {/*  disabled={completePayoff.length === 0}*/}
              {/*>*/}
              {/*  領収書発行*/}
              {/*</Button>*/}
              <Button
                style={{ marginLeft: "10px" }}
                variant="outlined"
                disabled={completePayoff.length === 0 || loading}
                onClick={() => downloadSpecPdf()}
              >
                {t("actions.exportPaymentNotice")}
              </Button>
            </Box>
            <Box display="flex" flexDirection="column">
              <Box display="flex">
                <Typography>
                  {t("summary.collectedAmountWithInvoice")}{" "}
                  {orders
                    .filter(
                      (order) =>
                        order.collectReceivable &&
                        EnumUtils.mapToEnum(PaymentType, order.paymentType) !==
                          PaymentType.creditCard
                    )
                    .reduce((sum, order) => (order.totalFee || 0) + sum, 0)}
                  {t("units.yen")}
                </Typography>
                <Typography style={{ marginLeft: "10px" }}>
                  {t("summary.uncollectedAmount")}{" "}
                  {orders
                    .filter(
                      (order) =>
                        !order.collectReceivable &&
                        EnumUtils.mapToEnum(PaymentType, order.paymentType) !==
                          PaymentType.creditCard
                    )
                    .reduce((sum, order) => (order.totalFee || 0) + sum, 0)}
                  {t("units.yen")}
                </Typography>
                <Typography style={{ marginLeft: "10px" }}>
                  {t("summary.cardPaymentAmount")}{" "}
                  {orders
                    .filter(
                      (order) =>
                        EnumUtils.mapToEnum(PaymentType, order.paymentType) ==
                        PaymentType.creditCard
                    )
                    .reduce((sum, order) => (order.totalFee || 0) + sum, 0)}
                  {t("units.yen")}
                </Typography>
                <Typography style={{ marginLeft: "10px" }}>
                  {t("summary.cardFee")}{" "}
                  {orders
                    .filter(
                      (order) =>
                        EnumUtils.mapToEnum(PaymentType, order.paymentType) ==
                        PaymentType.creditCard
                    )
                    .reduce((sum, order) => (order.cardFee || 0) + sum, 0)}
                  {t("units.yen")}
                </Typography>
              </Box>
              <Box display="flex">
                <Typography>
                  {t("summary.payoffAmount")}{" "}
                  {orders
                    .filter((order) => order.payoff)
                    .reduce((sum, order) => (order.totalFee || 0) + sum, 0)}
                  {t("units.yen")}
                </Typography>
                <Typography style={{ marginLeft: "10px" }}>
                  {t("summary.unpaidAmount")}{" "}
                  {orders
                    .filter((order) => !order.payoff)
                    .reduce((sum, order) => (order.totalFee || 0) + sum, 0)}
                  {t("units.yen")}
                </Typography>
              </Box>
              <Box display="flex">
                <Typography>
                  {t("summary.shopSales")}{" "}
                  {orders.reduce(
                    (sum, order) => (order.totalShopFee || 0) + sum,
                    0
                  )}
                  {t("units.yen")}
                </Typography>
                <Typography style={{ marginLeft: "10px" }}>
                  {t("summary.castReward")}{" "}
                  {orders.reduce(
                    (sum, order) => (order.totalCastFee || 0) + sum,
                    0
                  )}
                  {t("units.yen")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <Typography>{t("message.empty")}</Typography>
      )}
      {/* off-screen: PDF キャプチャ用。画面には表示しない。 */}
      <div
        style={{
          position: "fixed",
          top: -10000,
          left: -10000,
          pointerEvents: "none",
          opacity: 0,
        }}
        aria-hidden
      >
        <SpecPdf ref={specPdfRef} orders={orders} message={message} />
      </div>
    </>
  );
};
export default PayoffTable;
